import React, { useEffect, useState } from "react";
import "./landingpage.css";
import {
  FaMapMarkerAlt,
  FaLayerGroup,
  FaCity,
  FaRegClock,
  FaClock,
  FaChartLine,
  FaDatabase,
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaRulerVertical,
} from "react-icons/fa";
import Navbar from "./navbar_landingpage";

const Landingpage = () => {
  const [data, setData] = useState({
    wells: 0,
    divisions: 0,
    districts: 0,
    maxDuration: "0y 0m",
    minDuration: "0y 0m",
    avgDuration: "0y 0m",
    totalDataPoints: 0,
    maxHeight: "0ft",
    minHeight: "0ft",
    avgHeight: "0ft",
    maxDurationWellId: null,
    minDurationWellId: null,
    maxHeightWellId: null,
    minHeightWellId: null,
  });

  const convertDaysToYrsMonths = (days) => {
    if (!days || isNaN(days)) return "0y 0m";
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return `${years}y ${months}m`;
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          wellsRes,
          divRes,
          distRes,
          maxDurRes,
          minDurRes,
          avgDurRes,
          countRes,
          maxHRes,
          minHRes,
          avgHRes,
        ] = await Promise.all([
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/count"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/divisions/count"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/districts/count"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/max-observation-duration"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/min-observation-duration"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/wells/average-observation-duration"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/datalogs-table/total-entries"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/datalogs-table/max-groundwater-height"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/datalogs-table/min-groundwater-height"),
          fetch("https://warpo-dashboard.cse.buet.ac.bd/api/datalogs-table/average-groundwater-height"),
        ]);

        const [
          wells,
          divisions,
          districts,
          maxDuration,
          minDuration,
          avgDuration,
          totalDataPoints,
          maxHeight,
          minHeight,
          avgHeight,
        ] = await Promise.all([
          wellsRes.json(),
          divRes.json(),
          distRes.json(),
          maxDurRes.json(),
          minDurRes.json(),
          avgDurRes.json(),
          countRes.json(),
          maxHRes.json(),
          minHRes.json(),
          avgHRes.json(),
        ]);

        setData({
          wells: wells.totalWells || 0,
          divisions: divisions.totalDivisions || 0,
          districts: districts.totalDistricts || 0,
          maxDuration: convertDaysToYrsMonths(maxDuration.durationDays),
          minDuration: convertDaysToYrsMonths(minDuration.durationDays),
          avgDuration: convertDaysToYrsMonths(avgDuration.averageDurationDays),
          totalDataPoints: totalDataPoints.totalEntries || 0,
          maxHeight: `${parseFloat(maxHeight.maxGroundWaterHeightFeet).toFixed(1)}ft`,
          minHeight: `${parseFloat(minHeight.minGroundWaterHeightFeet).toFixed(1)}ft`,
          avgHeight: `${parseFloat(avgHeight.averageGroundWaterHeightFeet).toFixed(1)}ft`,
          maxDurationWellId: maxDuration.wellId,
          minDurationWellId: minDuration.wellId,
          maxHeightWellId: maxHeight.wellId, // <-- added
          minHeightWellId: minHeight.wellId, // <-- added
        });
      } catch (error) {
        console.error("Error fetching landing page data:", error);
      }
    };

    fetchStats();
  }, []);

  const stats = [
    {
      icon: <FaMapMarkerAlt />,
      label: "Total Number of Wells under Observation",
      value: data.wells,
      className: "blue-1",
    },
    {
      icon: <FaLayerGroup />,
      label: "Total Number of Divisions Covered",
      value: data.divisions,
      className: "blue-2",
    },
    {
      icon: <FaCity />,
      label: "Total Number of Districts Covered",
      value: data.districts,
      className: "blue-3",
    },
    {
      icon: <FaRegClock />,
      label: "Maximum Duration Covered by a Single Well",
      value: data.maxDuration,
      wellId: data.maxDurationWellId,
      className: "blue-4",
    },
    {
      icon: <FaClock />,
      label: "Minimum Duration Covered by a Single Well",
      value: data.minDuration,
      wellId: data.minDurationWellId,
      className: "blue-5",
    },
    {
      icon: <FaChartLine />,
      label: "Average Duration Covered by Wells",
      value: data.avgDuration,
      className: "blue-6",
    },
    {
      icon: <FaDatabase />,
      label: "Total Number of Data Points Collected",
      value: data.totalDataPoints.toLocaleString(),
      className: "blue-7",
    },
    {
      icon: <FaSortAmountUpAlt />,
      label: "Maximum Groundwater Height Recorded by a Single Well",
      value: data.maxHeight,
      wellId: data.maxHeightWellId, // <-- added
      className: "blue-8",
    },
    {
      icon: <FaSortAmountDown />,
      label: "Minimum Groundwater Height Recorded by a Single Well",
      value: data.minHeight,
      wellId: data.minHeightWellId, // <-- added
      className: "blue-9",
    },
    {
      icon: <FaRulerVertical />,
      label: "Average Groundwater Height Observed across All Wells",
      value: data.avgHeight,
      className: "blue-10",
    },
  ];

  return (
    <div className="landing-page">
      <Navbar />

      <main className="dashboard-container">
        <header className="dashboard-header">
          <p className="dashboard-eyebrow">WARPO observatory network</p>
          <h1 className="dashboard-title">Groundwater summary</h1>
          <p className="dashboard-subtitle">
            Consolidated statistics from monitored wells, coverage areas, and
            groundwater level records.
          </p>
        </header>

        <div className="dashboard-grid" role="list">
          {stats.map((stat, index) => (
            <article
              key={index}
              className={`dashboard-card ${stat.className}`}
              role="listitem"
            >
              <div className="dashboard-card-inner">
                <div className="stat-icon-wrap" aria-hidden="true">
                  {stat.icon}
                </div>
                <p className="stat-value">{stat.value}</p>
                <h2 className="stat-label">{stat.label}</h2>
                {stat.wellId && (
                  <div className="well-id-chip">
                    <FaMapMarkerAlt className="well-id-chip-icon" aria-hidden />
                    <span className="well-id-chip-text">{stat.wellId}</span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Landingpage;
